'use client'

import type { ReactNode } from 'react'

import { ImageWrapper } from '@/components/image'
import type { BrandLabProduct } from '@/lib/supabase/brand-modules/types'

import { LabFitToggle } from './lab-fit-toggle'
import { useLabFitScores } from './use-lab-fit-scores'

export interface LabScoreCardWideLabels {
  categories: Record<string, string>
  /** One-line per-category explanations (HearAdvisor metrics methodology). */
  explanations: Record<string, string>
  initialFit: string
  initialFitHint: string
  tunedFit: string
  tunedFitHint: string
  categoryHeadingInitial: string
  categoryHeadingTuned: string
  soundScore: string
  fullReport: string
  scaleNote: string
  methodologyLink: string
  /** Legend for the class-average tick marks, e.g. "OTC average" — empty
   *  string when the payload carries no class averages. */
  classAvgLabel: string
}

interface LabScoreCardWideProps {
  product: BrandLabProduct
  labels: LabScoreCardWideLabels
  reportHref: string | null
  /** Server-rendered HearAdvisor logo lockup (SVG gradient ids are built
   *  server-side), shown above the product name. */
  attribution?: ReactNode
}

const pct = (score: number) => `${Math.max(0, Math.min(100, (score / 5) * 100))}%`

// Small private bar (the compact card's LabScoreBar owns its own label column,
// which the wide row lays out differently). `marker` drops a tick at the
// product-class average for the same category and fit.
const ScoreBar = ({ score, marker }: { score: number; marker: number | null }) => (
  <span className="relative h-1.5 flex-1 rounded-full bg-white/12" aria-hidden>
    <span className="bg-sky-80 block h-full rounded-full" style={{ width: pct(score) }} />
    {marker != null && (
      <span
        className="absolute top-1/2 h-3.5 w-0.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/80"
        style={{ left: pct(marker) }}
      />
    )}
  </span>
)

// The article-width ("wide") variant of the lab score card: square product
// photo top-left, fit toggle in the header, and one spacious row per category
// with a plain-language explanation of what the score measures.
export const LabScoreCardWide = ({ product, labels, reportHref, attribution }: LabScoreCardWideProps) => {
  const { scores, activeFit, setSelectedFit } = useLabFitScores(product)
  const categoryHeading = activeFit === 'tuned' ? labels.categoryHeadingTuned : labels.categoryHeadingInitial
  const classAvgFor = (categoryId: string): number | null => {
    const entry = product.classAverages?.categories.find((c) => c.id === categoryId)
    if (!entry) return null
    return activeFit === 'tuned' ? entry.tunedAvg : entry.initialAvg
  }
  const showClassAvg = !!product.classAverages && labels.classAvgLabel !== ''

  return (
    <div className="h-fit rounded-2xl border border-white/12 bg-white/6 p-5 sm:p-7">
      <div className="flex flex-wrap items-center gap-x-5 gap-y-4">
        {product.image && (
          <div className="flex size-20 shrink-0 items-center justify-center rounded-xl bg-white p-2 sm:size-24">
            <ImageWrapper
              image={product.image}
              origin="brand-lab-product"
              loadTime="lazy"
              imgProps={{ alt: product.label, className: 'max-h-full max-w-full object-contain' }}
              sizingProps={{ fixed: false, minWidth: 96, maxWidth: 240, sizes: '96px' }}
            />
          </div>
        )}
        <div className="min-w-0 flex-1 basis-52">
          {/* flex, not block: the wrapper would otherwise inherit the article's
              prose-xl line-height and open a tall line box under the lockup. */}
          {attribution && <div className="mb-1.5 flex">{attribution}</div>}
          <p className="text-lg font-semibold text-white sm:text-xl">{product.label}</p>
          <p className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-white/70">
            {product.soundScore != null && (
              <>
                <span className="bg-sky-80/20 text-sky-80 inline-flex h-7 min-w-7 items-center justify-center rounded-lg px-1.5 text-sm font-bold">
                  {product.soundScore.toFixed(1)}
                </span>
                <span>{labels.soundScore}</span>
              </>
            )}
            {reportHref && (
              <a href={reportHref} className="text-sky-80 font-semibold hover:underline" data-tracking="ignore">
                {labels.fullReport} →
              </a>
            )}
          </p>
        </div>
        <div className="w-full sm:w-64">
          <LabFitToggle
            activeFit={activeFit}
            availability={{ initial: scores.initial.length > 0, tuned: scores.tuned.length > 0 }}
            labels={{ initial: labels.initialFit, tuned: labels.tunedFit }}
            ariaLabel={categoryHeading}
            onSelect={setSelectedFit}
          />
          <p className="mt-1.5 text-center text-xs text-white/60">
            {activeFit === 'tuned' ? labels.tunedFitHint : labels.initialFitHint}
          </p>
        </div>
      </div>

      <div className="mt-4 flex flex-col divide-y divide-white/10">
        {scores[activeFit].map((category) => {
          const classAvg = showClassAvg ? classAvgFor(category.id) : null
          return (
            <div
              key={category.id}
              className="grid gap-x-8 gap-y-2 py-4 sm:grid-cols-[minmax(0,1fr)_minmax(0,17rem)] sm:items-center"
            >
              <div>
                <p className="text-md font-medium text-white">{labels.categories[category.id] ?? category.id}</p>
                <p className="mt-0.5 text-sm leading-5 text-pretty text-white/70">
                  {labels.explanations[category.id] ?? ''}
                </p>
              </div>
              <div
                className="flex items-center gap-3"
                title={classAvg != null ? `${labels.classAvgLabel}: ${classAvg.toFixed(1)}` : undefined}
              >
                <ScoreBar score={category.score} marker={classAvg} />
                <span className="text-md w-8 text-right font-semibold text-white tabular-nums">
                  {category.score.toFixed(1)}
                </span>
              </div>
            </div>
          )
        })}
      </div>

      {/* div, not p: the article wrapper caps every descendant <p> at 620px
          ([&_p]:max-w-[620px]), which would stop the right-aligned link short
          of the card edge. All read-me fine print lives here: the scale note
          with the tick key (a miniature of the actual track) stacked under
          it, and the methodology link on the right. */}
      <div className="flex flex-wrap items-end justify-between gap-x-4 gap-y-2 border-t border-white/12 pt-3.5 text-xs text-white/60">
        <div className="flex flex-col gap-1.5">
          {showClassAvg && (
            <span className="flex items-center gap-2">
              <span className="relative h-1.5 w-10 rounded-full bg-white/12" aria-hidden>
                <span className="absolute top-1/2 left-1/2 h-3.5 w-0.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/80" />
              </span>
              {labels.classAvgLabel}
            </span>
          )}
          <span>{labels.scaleNote}</span>
        </div>
        <a
          href="https://www.hearadvisor.com/methods/hearing-devices/metrics"
          className="text-sky-80 font-semibold hover:underline"
          data-tracking="ignore"
          target="_blank"
          rel="noopener noreferrer"
        >
          {labels.methodologyLink} →
        </a>
      </div>
    </div>
  )
}
